// src/services/soilService.js
// Fetches soil sensor data from the AfriClimate backend (/api/v1/soil/*)

import { AFRICLIMATE_API } from './config';
import authService from './authService';

const SOIL_BASE = `${AFRICLIMATE_API.BASE_URL}/soil`;

const get = async (path) => {
  const res = await fetch(`${SOIL_BASE}${path}`);
  if (!res.ok) throw new Error(`Soil API ${path}: HTTP ${res.status}`);
  return res.json();
};

/** All soil devices with their latest reading. */
export const fetchSoilDevices = async () => {
  const json = await get('/devices');
  return json.data || [];
};

/** Soil devices assigned to the current user (requires auth token). */
export const fetchMySoilDevices = async () => {
  const res = await fetch(`${SOIL_BASE}/devices`, {
    headers: { ...authService.getAuthHeaders() },
  });
  if (!res.ok) throw new Error(`Soil devices: HTTP ${res.status}`);
  const json = await res.json();
  return json.data || [];
};

/** Latest reading from every soil device. */
export const fetchLatestSoilReadings = async () => {
  const json = await get('/readings/latest');
  return json.data || [];
};

/** Historical readings for one soil device (default last 7 days). */
export const fetchSoilReadings = async (deviceId, options = {}) => {
  const params = new URLSearchParams();
  if (options.limit)     params.set('limit',     options.limit);
  if (options.startDate) params.set('startDate', options.startDate);
  const qs = params.toString() ? `?${params}` : '';
  const json = await get(`/readings/device/${deviceId}${qs}`);
  return json.data || [];
};

/**
 * Soil readings colocated with a weather station (for dashboard integration).
 * Returns { device, readings } or null if no soil sensor found for this station.
 */
export const fetchSoilForStation = async (weatherStationId, options = {}) => {
  try {
    const params = new URLSearchParams();
    if (options.limit)     params.set('limit',     options.limit || 48);
    if (options.startDate) params.set('startDate', options.startDate);
    const qs = params.toString() ? `?${params}` : '';
    const json = await get(`/readings/station/${weatherStationId}${qs}`);
    if (!json.data || json.data.length === 0) return null;
    return json.data;
  } catch {
    return null;
  }
};

/** Update a soil device (name, weatherStationId, location, depth_cm, assignedUsers). */
export const updateSoilDevice = async (deviceId, fields) => {
  const headers = {
    'Content-Type': 'application/json',
    ...authService.getAuthHeaders(),
  };
  const res = await fetch(`${SOIL_BASE}/devices/${encodeURIComponent(deviceId)}`, {
    method: 'PATCH',
    headers,
    body: JSON.stringify(fields),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || `Soil PATCH ${deviceId}: HTTP ${res.status}`);
  }
  return res.json();
};

/** Human-readable label for each soil field. */
export const SOIL_LABELS = {
  hum:        { label: 'Soil Moisture',  unit: '%',       color: '#3b82f6' },
  temp:       { label: 'Soil Temp',      unit: '°C',      color: '#ef4444' },
  ec:         { label: 'Conductivity',   unit: 'µS/cm',   color: '#8b5cf6' },
  ph:         { label: 'pH',             unit: '',        color: '#10b981' },
  nitro:      { label: 'Nitrogen',       unit: 'mg/kg',   color: '#84cc16' },
  phos:       { label: 'Phosphorus',     unit: 'mg/kg',   color: '#f97316' },
  pota:       { label: 'Potassium',      unit: 'mg/kg',   color: '#eab308' },
  battery_mv: { label: 'Battery',        unit: 'mV',      color: '#6b7280' },
};
